/** Lead type / source labels, form options, and badge styling (single source of truth). */

export const CHANNEL_LEAD_TYPES = {
  LD: ['fresh', 'aged', 'fex', 'iul', 'mortgage_protection', 'emergency', 'warm', 'recruit', 'referral'],
  IM: ['f_and_f', 'top_25', 'hot_market', 'networking', 'event', 'social_media', 'emergency', 'warm', 'recruit', 'referral'],
  AM: [
    'life_insurance_agent',
    'real_estate_agent',
    'mortgage_professional',
    'accountant',
    'notary',
    'google_business',
    'prophog',
    'warm',
    'recruit',
    'referral',
  ],
};

export const CHANNEL_LABELS = {
  LD: 'Leads Division',
  IM: 'Inspirational Marketing',
  AM: 'Advance Markets',
};

export const CHANNEL_COLORS = {
  LD: '#00AAFF',
  IM: '#22C55E',
  AM: '#7C3AED',
};

export const CHANNEL_VALUES = ['IM', 'AM', 'LD'];

export const LEAD_TYPE_VALUES = [
  'fresh',
  'aged',
  'warm',
  'referral',
  'event',
  'emergency',
  'recruit',
  'hot_market',
  'top_25',
  'f_and_f',
  'networking',
  'social_media',
  'prophog',
  'fex',
  'iul',
  'mortgage_protection',
  'life_insurance_agent',
  'real_estate_agent',
  'mortgage_professional',
  'accountant',
  'notary',
  'google_business',
];

export const VALID_LEAD_TYPES = new Set(LEAD_TYPE_VALUES);

export const LEAD_TYPE_LABELS = {
  fresh: 'Fresh',
  aged: 'Aged',
  warm: 'Warm',
  referral: 'Referral',
  event: 'Event',
  emergency: 'Emergency',
  recruit: 'Recruit',
  hot_market: 'Hot Market',
  top_25: 'Top 25',
  f_and_f: 'F&F',
  networking: 'Networking',
  social_media: 'Social Media',
  prophog: 'Prophog',
  fex: 'FEX',
  iul: 'IUL',
  mortgage_protection: 'Mortgage Protection',
  life_insurance_agent: 'Life Insurance Agent',
  real_estate_agent: 'Real Estate Agent',
  mortgage_professional: 'Mortgage Professional',
  accountant: 'Accountant',
  notary: 'Notary',
  google_business: 'Google Business',
};

/** Badge: `variant` uses Kit Badge presets; `color`/`textColor` for custom hex. */
export const LEAD_TYPE_BADGE = {
  fresh: { label: 'Fresh', variant: 'success' },
  warm: { label: 'Warm', variant: 'warning' },
  aged: { label: 'Aged', variant: 'default' },
  referral: { label: 'Referral', variant: 'primary' },
  event: { label: 'Event', variant: 'purple' },
  emergency: { label: 'Emergency', variant: 'danger' },
  recruit: { label: 'Recruit', variant: 'teal' },
  hot_market: { label: 'Hot Market', color: '#FF4500', textColor: '#ffffff' },
  top_25: { label: 'Top 25', color: '#FFD700', textColor: '#1f2937' },
  f_and_f: { label: 'F&F', color: '#FF69B4', textColor: '#ffffff' },
  networking: { label: 'Networking', color: '#6366F1', textColor: '#ffffff' },
  social_media: { label: 'Social Media', color: '#0EA5E9', textColor: '#ffffff' },
  prophog: { label: 'Prophog', color: '#7C3AED', textColor: '#ffffff' },
  fex: { label: 'FEX', color: '#0EA5E9', textColor: '#ffffff' },
  iul: { label: 'IUL', color: '#6366F1', textColor: '#ffffff' },
  mortgage_protection: { label: 'Mortgage Protection', color: '#F59E0B', textColor: '#1f2937' },
  life_insurance_agent: { label: 'Life Insurance Agent', color: '#7C3AED', textColor: '#ffffff' },
  real_estate_agent: { label: 'Real Estate Agent', color: '#10B981', textColor: '#ffffff' },
  mortgage_professional: { label: 'Mortgage Professional', color: '#3B82F6', textColor: '#ffffff' },
  accountant: { label: 'Accountant', color: '#8B5CF6', textColor: '#ffffff' },
  notary: { label: 'Notary', color: '#EC4899', textColor: '#ffffff' },
  google_business: { label: 'Google Business', color: '#EF4444', textColor: '#ffffff' },
};

export const LEAD_TYPE_FORM_OPTIONS = LEAD_TYPE_VALUES.map((value) => ({
  value,
  label: LEAD_TYPE_LABELS[value],
}));

export const LEAD_TYPE_FILTER_OPTIONS = [
  { label: 'All Types', value: '' },
  ...LEAD_TYPE_FORM_OPTIONS.filter((o) => o.value !== 'recruit'),
];

export const LEAD_SOURCE_VALUES = [
  'facebook_ad',
  'csv_upload',
  'manual',
  'referral',
  'event',
  'purchased',
  'social_media',
  'networking',
  'prophog',
  'in_person',
];

export const VALID_LEAD_SOURCES = new Set(LEAD_SOURCE_VALUES);

export const LEAD_SOURCE_LABELS = {
  facebook_ad: 'Facebook Ad',
  csv_upload: 'CSV Upload',
  manual: 'Manual',
  referral: 'Referral',
  event: 'Event',
  purchased: 'Purchased',
  social_media: 'Social Media',
  networking: 'Networking',
  prophog: 'Prophog',
  in_person: 'In Person',
};

export const LEAD_SOURCE_FORM_OPTIONS = LEAD_SOURCE_VALUES.map((value) => ({
  value,
  label: LEAD_SOURCE_LABELS[value],
}));

const LEAD_TYPE_ALIASES = {
  hotmarket: 'hot_market',
  'hot market': 'hot_market',
  top25: 'top_25',
  'top 25': 'top_25',
  fandf: 'f_and_f',
  'f&f': 'f_and_f',
  'f and f': 'f_and_f',
  ff: 'f_and_f',
  socialmedia: 'social_media',
  'social media': 'social_media',
  mortgageprotection: 'mortgage_protection',
  'mortgage protection': 'mortgage_protection',
  lifeinsuranceagent: 'life_insurance_agent',
  'life insurance agent': 'life_insurance_agent',
  realestateagent: 'real_estate_agent',
  'real estate agent': 'real_estate_agent',
  mortgageprofessional: 'mortgage_professional',
  'mortgage professional': 'mortgage_professional',
  googlebusiness: 'google_business',
  'google business': 'google_business',
};

const LEAD_SOURCE_ALIASES = {
  socialmedia: 'social_media',
  'social media': 'social_media',
  inperson: 'in_person',
  'in person': 'in_person',
  'in-person': 'in_person',
  facebook: 'facebook_ad',
  'facebook ad': 'facebook_ad',
  csv: 'csv_upload',
  upload: 'csv_upload',
};

function normalizeToken(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

export function sanitizeLeadType(value, isRecruit = false) {
  if (isRecruit) return 'recruit';
  const raw = normalizeToken(value).replace(/\s+/g, '_');
  const aliased = LEAD_TYPE_ALIASES[normalizeToken(value)] || LEAD_TYPE_ALIASES[raw] || raw;
  if (VALID_LEAD_TYPES.has(aliased)) return aliased;
  return 'fresh';
}

export function sanitizeLeadSource(value) {
  const token = normalizeToken(value);
  const underscored = token.replace(/\s+/g, '_');
  const aliased = LEAD_SOURCE_ALIASES[token] || LEAD_SOURCE_ALIASES[underscored] || underscored;
  if (VALID_LEAD_SOURCES.has(aliased)) return aliased;
  return 'manual';
}

export function formatLeadSourceLabel(source) {
  return LEAD_SOURCE_LABELS[source] || String(source || '').replace(/_/g, ' ') || '—';
}

/** Lead types allowed for a business channel (empty channel returns all types). */
export function leadTypesForChannel(channel) {
  if (!channel || !CHANNEL_LEAD_TYPES[channel]) return LEAD_TYPE_VALUES;
  return CHANNEL_LEAD_TYPES[channel];
}

/** Apply exclusivity_status on aged leads during CSV import (column used when added in DB). */
export function applyAgedExclusivity(lead, markAgedNonExclusive) {
  if (!markAgedNonExclusive || lead?.lead_type !== 'aged') return lead;
  return { ...lead, exclusivity_status: 'non_exclusive' };
}

export function applyAgedExclusivityToLeads(leads, markAgedNonExclusive) {
  if (!markAgedNonExclusive) return leads;
  return leads.map((lead) => applyAgedExclusivity(lead, true));
}
